import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ElementRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import OlMap from 'ol/Map';
import Overlay from 'ol/Overlay';
import { fromLonLat } from 'ol/proj';

export type PopupSize    = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
export type PopupVariant = 'primary' | 'secondary' | 'tertiary';
export type PopupColor   = 'amarante' | 'moonstone' | 'rose' | 'mint'
                         | 'semantic-red' | 'semantic-yellow' | 'semantic-green';

@Component({
  selector:    'app-popup-panel',
  templateUrl: './popup-panel.component.html',
  styleUrls:   ['./popup-panel.component.scss'],
  standalone:  true,
  imports:     [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartePopupComponent implements OnChanges {

  // ── Inputs ────────────────────────────────────────────────────────────────

  /** Instance OL Map — nécessaire pour créer et attacher l'Overlay */
  @Input() map!: OlMap;

  /**
   * Coordonnées géographiques [lon, lat] du point d'ancrage.
   * La popup s'affiche à droite de ce point (center-left + offset).
   * Passer null pour masquer sans fermer.
   */
  @Input() position: [number, number] | null = null;

  /** Ouvre ou ferme la popup */
  @Input() isOpen = false;

  @Input() showHeader = true;

  /** Variante visuelle du header */
  @Input() variant: PopupVariant = 'tertiary';

  /** Couleur du thème */
  @Input() color: PopupColor = 'amarante';

  /**
   * Largeur de la popup carte :
   * xsmall=180px | small=240px | medium=300px | large=360px | xlarge=440px
   */
  @Input() size: PopupSize = 'small';

  /** Afficher une icône à gauche du titre (contenu via slot="header-icon-left") */
  @Input() addIconLeft = false;

  /** Afficher une icône à droite du titre (contenu via slot="header-icon-right") */
  @Input() addIconRight = false;

  /** Décalage en pixels [x, y] par rapport au point d'ancrage (défaut : [20, 0]) */
  @Input() offset: [number, number] = [20, 0];

  // ── Output ────────────────────────────────────────────────────────────────

  /** Émis au clic sur ✕ ou Escape */
  @Output() closed = new EventEmitter<void>();

  // ── Ref DOM ───────────────────────────────────────────────────────────────
  private popupEl = viewChild<ElementRef<HTMLDivElement>>('cartePopupEl');

  // ── État animation ────────────────────────────────────────────────────────
  isVisible      = false;
  isAnimatingOut = false;

  // ── OL Overlay (créé une fois, réutilisé) ────────────────────────────────
  private overlay: Overlay | null = null;
  private overlayAttached = false;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.show();
      } else {
        this.triggerClose();
      }
    }

    if (changes['position'] && this.isOpen) {
      this.updateOverlayPosition();
    }

    // Première fois que map est disponible → créer l'overlay
    if (changes['map'] && this.map && !this.overlayAttached) {
      this.attachOverlay();
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  close(): void {
    this.closed.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.close();
  }

  // ── Classes BEM ───────────────────────────────────────────────────────────

  get popupClasses(): Record<string, boolean> {
    return {
      'popup-panel':                 true,
      [`popup-panel--${this.size}`]: true,
      'popup-panel--out':            this.isAnimatingOut,
    };
  }

  get headerClasses(): Record<string, boolean> {
    return {
      'popup-panel__header':                    true,
      [`popup-panel__header--${this.variant}`]: true,
      [`popup-panel__header--${this.color}`]:   true,
    };
  }

  // ── Privé ─────────────────────────────────────────────────────────────────

  private show(): void {
    this.isVisible      = true;
    this.isAnimatingOut = false;

    // Attache l'overlay si ce n'est pas encore fait
    if (!this.overlayAttached && this.map) {
      this.attachOverlay();
    }
    this.updateOverlayPosition();
  }

  private triggerClose(): void {
    if (!this.isVisible) return;
    this.isAnimatingOut = true;
    this.overlay?.setPosition(undefined); // retire immédiatement de la carte
    setTimeout(() => {
      this.isVisible      = false;
      this.isAnimatingOut = false;
    }, 200);
  }

  // Crée l'OL Overlay et l'attache à la carte une seule fois
  private attachOverlay(): void {
    const el = this.popupEl()?.nativeElement;
    if (!el || !this.map) return;

    this.overlay = new Overlay({
      element:     el,
      positioning: 'center-left', // popup à droite du point d'ancrage
      offset:      this.offset,
      stopEvent:   true,          // clics dans la popup non propagés à la carte
      autoPan:     {
        animation: { duration: 250 },
      },
    });

    this.map.addOverlay(this.overlay);
    this.overlayAttached = true;
  }

  // Met à jour la position de l'overlay selon [position]
  private updateOverlayPosition(): void {
    if (!this.overlay) return;
    if (this.position) {
      this.overlay.setPosition(fromLonLat(this.position));
    } else {
      this.overlay.setPosition(undefined);
    }
  }
}
